/** Canonical API operation scopes used to build role permissions. */

export const CASE_SCOPES = {
  /** Opret sag */
  createCase: 'create-case',
  /** Hent sag */
  getCase: 'get-case',
  /** Hent aktive sager */
  getActiveCases: 'get-active-cases',
  /** Hent aktive sager lite */
  getActiveCasesLite: 'get-active-cases-lite',
  /** Hent sager for kontrakt nummer */
  getContractCases: 'get-contract-cases',
  /** Hent sager på kontakt */
  getContactCases: 'get-contact-cases',
  /** Hent sager på kunde */
  getCompanyCases: 'get-company-cases',
  /** Hent sags typer */
  getCaseEventDescriptions: 'get-case-event-descriptions',
  /** Hent afslutningsårsager */
  getCaseCloseReasons: 'get-case-close-reasons',
  /** Send tillæg til låneaftale */
  sendAddendum: 'send-addendum',
  /** Send beregning */
  sendCalculation: 'send-calculation',
  /* Luk sag */
  closeCase: 'close-case',
  /* Overtag sag */
  takeOverCase: 'take-over-case',
  /* Opdater kontaktperson */
  updateContact: 'update-contact',
  /* Rul tilbage til beregningsfasen */
  restartCalculationPhase: 'restart-calculation-phase',
  /* Start eksekveringsfasen */
  startExecutionPhase: 'start-execution-phase',
  startExecutionPhaseWithCompensationAmount:
    'start-execution-phase-with-compensation-amount',
  startExecutionPhaseWithInterestRate:
    'start-execution-phase-with-interest-rate',
  /* Opret note */
  createNote: 'create-note',
  /* Send til finans */
  notifyFinance: 'notify-finance',
  /* Afvis prissætning */
  rejectPricing: 'reject-pricing',
  /* Gennemfør indfrielse */
  completeRedemption: 'complete-redemption',
  /* Gennemfør indfrielse */
  sendAddendumToAgreement: 'send-addendum-to-agreement',
  /** Send forslag til aftale */
  sendAgreementProposal: 'send-agreement-proposal',
  /* Hent faseansvarstildelinger */
  getPhaseAssignees: 'get-phase-assignees',
  /** Lav ny udlånssag */
  createContractDraft: 'create-contract-draft',
  /** Igangsæt KYC */
  initiateKyc: 'initiate-kyc',
  /** skift fase status */
  changePhaseState: 'change-phase-state',
  /** Hent saginformation statusser */
  caseInformationStates: 'case-information-states',
  /** Opdater en sags fuldmagtshavere  */
  updateCaseMandates: 'update-case-mandates',
  /** Vælg underskrivere for en udlånssag  */
  updateCaseSigners: 'update-case-signers',
  /** Vælg underskrivere for garantistillere på en udlånssag  */
  updateCaseSignersByGuarantor: 'update-case-signers-by-guarantor',
  /** Hent nyt lån sags underskrivere */
  getCaseSigners: 'get-case-signers',
  /** Hent nyt lån sags underskrivere for virksomheder */
  getCaseSignersByCompany: 'get-case-signers-by-company',
  /** Hent nyt lån sags underskrivere for garanter */
  getCaseSignersByGuarantor: 'get-case-signers-by-guarantor',
  /** Hent underskriftsfrister */
  getSigningDates: 'get-signing-dates',
  /** Hent underskrift status */
  getSigningStatus: 'get-signing-status',
  /** Hent status om sagen er klar til at accepteres */
  getIsReadyForAgreement: 'is-ready-for-agreement',
  /** Send underskrift påmindelse */
  sendSigningReminder: 'send-signing-reminder',
  /** Hent mulige nye frister */
  getPossibleNewDeadlines: 'get-possible-new-deadlines',
  /** Juster frist for sagsunderskrift */
  updateCaseSigningDeadline: 'update-case-signing-deadline',
  /** Hent aktuelle vedtægter for virksomhed */
  getDocumentByCompanyNumberCurrentbylaw:
    'get-document-by-company-number-currentbylaw',
} as const;

export const COMPANY_SCOPES = {
  /** Hent relationer */
  getRelations: 'get-relations',
  /** Hent kunde */
  getCompany: 'get-company',
  /** Hent kontakter for kunde */
  getCompanyContacts: 'get-company-contacts',
  /** Hent alle kontakter i CRM */
  getAllContacts: 'get-all-contacts',
  /** Hent én kontakt */
  getContact: 'get-contact',
  /** Hent alle aftaler for en kunde */
  getLoans: 'get-loans',
  /** Hent lån til oprettelse af nye sager */
  getEligibleLoans: 'get-eligible-loans',
  /** Hent bevillinger til oprettelse af nye lån */
  getAllocations: 'get-allocations',
  /** Hent én bevilling */
  getAllocation: 'get-allocation',
  /** Hent én bevillingssag */
  getAllocationCase: 'get-allocation-case',
  /** Hent garantistillere */
  getGuarantors: 'get-guarantors',
  /** opdater garantistillere */
  updateGuarantorDetails: 'update-guarantor-details',
  /** Hent garantistillere */
  getGuarantorDetails: 'get-guarantor-details',
  /** Hent interessenter */
  getCompanyPartnershipLiability: 'get-company-partnership-liability',
  /** Hent detaljer om partnerskab */
  getPartnershipLiabilityDetails: 'get-partnership-liability-details',
  /* opdater interessenter */
  updatePartnershipLiabilityDetails: 'update-partnership-liability-details',
  /* hent betalingsdetaljer */
  getPaymentDetails: 'get-payment-details',
  /* hent kontaktperson for nyt lån  */
  getReminderResponsible: 'get-reminder-responsible',
  /** Hent virksomhedens underskrivere */
  getCompanySigners: 'get-company-signers',
  /** Hent eksterne virksomhedsbrugere */
  getExternalCompanyUsers: 'get-external-company-users',
} as const;

export const LOAN_SCOPES = {
  /** Hent aftaledata */
  getLoanInfo: 'get-loan-info',
  /** Hent aftaledata for kreditter */
  getCreditInfo: 'get-credit-info',
  /** Hent alle lån for en kunde */
  getLoanPortfolio: 'get-loan-portfolio',
  /** Hent gyldige betalingsdatoer */
  getPartialRedemptionDates: 'get-partial-redemption-dates',
  /** Hent første interessepayment */
  getFirstInterestPaymentDates: 'get-first-interest-payment-dates',
  /** Lav beregningsgrundlag */
  createCalculationBasis: 'create-calculation-basis',
  /** Lav beregning */
  createCalculation: 'create-calculation',
  /** Opdater beregning */
  updateCalculation: 'update-calculation',
  /** Hent beregning */
  getCalculation: 'get-calculation',
  /** Valider beregning */
  validateCalculation: 'validate-calculation',
  /** Slet beregning */
  deleteCalculation: 'delete-calculation',
  /** Eksporter beregning */
  exportCalculation: 'export-calculation',
  /** Eksporter portefølje */
  exportLoanPortfolio: 'export-loan-portfolio',
  /** Hent EAN information */
  getEanInformation: 'get-ean-information',
  /** Opdater lånekladde */
  updateContractDraft: 'update-contract-draft',
  /** Hent lånekladde */
  getContractDraft: 'get-contract-draft',
  /** Opdater bevilling */
  updateAllocation: 'update-allocation',
  /** Opdater betalingsdetaljer */
  updatePaymentDetails: 'update-payment-details',
  /** Opdater industrikode */
  updateContractDraftIndustryCode: 'update-contract-draft-industry-code',
  /** Hent låneparametre */
  getLoanParameters: 'get-loan-parameters',
  /** Flyt underskriftsfrist i nyt lån  */
  moveNewLoanSigningDeadline: 'move-new-loan-signing-deadline',
} as const;

export const APPLICATION_SCOPES = {
  /** Hent indstillinger */
  getConfigSettings: 'get-config-settings',
  /** Hent branchekoder */
  getIndustryCodes: 'get-industry-codes',
} as const;

export const DOCUMENT_SCOPES = {
  /** Dan email med vedhæftet beregning eller påmindelse */
  createEmail: 'create-email',
  /** Ommærkning af dokument */
  labelDocument: 'label-document',
  /** Hent dokument */
  getDocument: 'get-document',
} as const;

export const SUPER_USER_SCOPES = {
  /* Hent valgbare datoer, som sagens udbetalingsdato kan rykkes til */
  getSigningEligiblePayoutDates: 'get-signing-eligible-payout-dates',
  /* Flyt underskriftsfrist i nyt lån  */
  moveNewLoanSigningDeadlineAndPayoutDate:
    'move-new-loan-signing-deadline-and-payout-date',
  /* Gennemtving ny renteaftale */
  forceInterestAgreement: 'force-interest-agreement',
} as const;

export const ALL_SCOPES = {
  ...CASE_SCOPES,
  ...COMPANY_SCOPES,
  ...LOAN_SCOPES,
  ...APPLICATION_SCOPES,
  ...DOCUMENT_SCOPES,
} as const;

export type Scope =
  | (typeof ALL_SCOPES)[keyof typeof ALL_SCOPES]
  | (typeof SUPER_USER_SCOPES)[keyof typeof SUPER_USER_SCOPES];
